/**
 * King Living Store Locator Theme
 *
 * Store listing page styling system:
 * - Layout: Search section (1/3) | Map (2/3)
 * - Store list below search section
 * - Responsive design with mobile map below
 * - Store tile component styling
 *
 * @override Extends Chakra UI theme
 */

const Showrooms = {
  parts: [
    'container',
    'grid',
    'leftColumn',
    'rightColumn',
    'mobileMap',
    'contentStack',
    'heading',
    'searchContainer',
    'searchInput',
    'searchDropdown',
    'dropdownItem',
    'dropdownItemText',
    'sectionHeader',
    'sectionHeaderWithMargin',
    'storeList',
    'loadingContainer',
    'loadingStack',
    'loadingText',
    'emptyState',
    'errorContainer',
    'headingContainer',
    'emptyStateContainer',
    'myShowroomLabel',
    // Store Tile parts
    'myShowroomTileContainer',
    'storeTileContainer',
    'storeTileContentStack',
    'storeTileStoreName',
    'storeTileInfoRow',
    'storeTileIcon',
    'storeTileInfoText',
    'storeTileActionsRow',
    'storeTileButton',
    'storeTileButtonActive',
    'storeTileLink',
    'storeTileLinkRow'
  ],
  baseStyle: {
    container: {
      bg: 'brandColors.primaryBG',
      minHeight: '100vh',
    },
    grid: {
      gap: 0,
      minH: {base: 'auto', lg: '100vh'},
      templateColumns: {base: '1fr', lg: '480px 1fr'},
    },
    leftColumn: {
      bg: 'white',
      borderRightColor: {base: 'transparent', lg: 'gray.200'},
      borderRightWidth: {base: '0', lg: '1px'},
      maxH: {base: 'none', lg: 'calc(100vh)'},
      overflowY: {base: 'visible', lg: 'auto'},
    },
    rightColumn: {
      display: {base: 'none', lg: 'block'},
      h: 'calc(100vh)',
    },
    mobileMap: {
      display: {base: 'block', lg: 'none'},
      h: '258px',
      w: '100%',
    },
    contentStack: {
      align: 'stretch',
    },
    heading: {
      fontSize: '24px',
      fontWeight: '500',
      lineHeight: '120%',
      letterSpacing: '-0.24px',
    },
    searchContainer: {
      position: 'relative',
    },
    searchInput: {
      bg: 'white',
      borderColor: 'brandColors.greyscale10',
      color: 'brandColors.greyscale80',
      fontSize: '14px',
      size: 'lg',
      paddingY: '18px',
      height: '54px',
      _focus: {
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
      },
      _hover: {
        borderColor: 'brandColors.greyscale10',
      },
    },
    searchDropdown: {
      bg: 'white',
      borderColor: 'gray.200',
      borderRadius: 'md',
      borderWidth: '1px',
      boxShadow: 'lg',
      left: 0,
      mt: 1,
      position: 'absolute',
      right: 0,
      zIndex: 10,
    },
    dropdownItem: {
      _hover: {bg: 'gray.50'},
      cursor: 'pointer',
      p: 3,
    },
    dropdownItemText: {
      color: 'gray.700',
      fontSize: 'sm',
    },
    sectionHeaderWithMargin: {
      color: 'gray.500',
      fontSize: 'xs',
      fontWeight: 'semibold',
      letterSpacing: 'wide',
      mt: 4,
      textTransform: 'uppercase',
    },
    storeList: {
      align: 'stretch',
      backgroundColor: 'brandColors.primaryBG',
      spacing: {base: '8px', lg: 0},
    },
    loadingContainer: {
      align: 'center',
      justify: 'center',
      py: 8,
    },
    loadingStack: {
      spacing: 3,
    },
    loadingText: {
      color: 'gray.500',
      fontSize: 'sm',
    },
    emptyState: {
      borderRadius: 'md',
    },
    errorContainer: {
      p: 8,
      textAlign: 'center',
    },
    headingContainer: {
      bg: 'brandColors.primaryBG',
      color: 'black',
      padding: {base: '40px 20px', lg: '48px'},
      gap: '16px',
      display: 'flex',
      flexDirection: 'column',
    },
    emptyStateContainer: {
      p: {base: '32px', lg: '48px'},
    },
    myShowroomLabel: {
      backgroundColor: 'rgba(198, 189, 161, 0.15)',
      borderRadius: '4px',
      color: 'brandColors.greyscale90',
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '120%',
      padding: '6px 14px',
      display: 'inline-flex',
      gap: '8px',
      justifyContent: 'center',
      alignItems: 'center',
    },
    // Store Tile styles
    myShowroomTileContainer: {
      backgroundColor: 'whiteAlpha.900',
      sx: {
        '.store-tile': {
          backgroundColor: 'whiteAlpha.900',
        },
      },
    },
    storeTileContainer: {
      bg: 'brandColors.primaryBG',
      borderColor: 'brandColors.greyscale10',
      borderBottomWidth: '1px',
      color: 'brandColors.greyscale90',
      padding: {base: '24px 20px 24px 32px', lg: '32px 48px'},
    },
    storeTileContentStack: {
      align: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    storeTileContentContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    storeTileStoreName: {
      fontSize: {base: '18px', lg: '20px'},
      fontWeight: '500',
      lineHeight: '120%',
    },
    storeTileInfoContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    storeTileInfoRow: {
      align: 'flex-start',
      spacing: '10px',
    },
    storeTileIcon: {
      boxSize: '16px',
      color: 'brandColors.primaryDarkLinen',
      fill: 'brandColors.primaryDarkLinen',
      flexShrink: 0,
    },
    storeTileInfoText: {
      fontSize: '14px',
    },
    storeTileActionsRow: {
      align: 'center',
      gap: '24px',
      justify: 'flex-start',
    },
    storeTileButton: {
      backgroundColor: 'transparent',
      borderColor: 'brandColors.greyscale10',
      color: 'brandColors.greyscale',
      colorScheme: 'dark',
      size: 'sm',
    },
    storeTileButtonActive: {
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'black',
      color: 'white',
      sx: {
        '& svg': {
          color: 'white',
        },
        '&:hover': {
          '& svg': {
            color: 'brandColors.greyscale',
          },
        },
      },
    },
    storeTileLink: {
      color: 'brandColors.primaryDarkLinen',
      fontSize: '16px',
      fontWeight: '500',
      lineHeight: '120%',
      _hover: {
        textDecoration: 'underline',
      },
    },
  },
};

export default Showrooms;
